import PropTypes from 'prop-types';
import React, { Component } from 'react';

import {
  DAY_IN_MILLISECONDS,
  HOUR_IN_MILLISECONDS,
  MINUTE_IN_MILLISECONDS,
} from 'utils/intervals';
import {
  clientSupportsStorage,
  isStorageAvailable,
  retrieve,
  store,
} from 'utils/storage';

import logout from '../../js/adobe-primetime/logout';
import sendEvent from '../../js/adobe-primetime/sendEvent';
import getErrorMessage from '../../js/adobe-primetime/errorHandler';

import settings from './settings';
import Providers from './Providers';
import FooterLinkPortal from './FooterLinkPortal';
import PlayerOverlay from './PlayerOverlay';
import ConcurrencyHandler from './ConcurrencyHandler';

import './adobe-primetime.scss';

/**
 * Get the number of milliseconds pertaining to a unit of time.
 * @param unit
 * @returns {number}
 */
const unitToMilliseconds = (unit) => {
  switch (unit) {
    case 'minutes':
      return MINUTE_IN_MILLISECONDS;
    case 'hours':
      return HOUR_IN_MILLISECONDS;
    case 'days':
      return DAY_IN_MILLISECONDS;
    default:
      return 0;
  }
};

/**
 * Primary providers as defined in Primetime settings.
 *
 * @type {array}
 */
const PRIMARY_PROVIDERS = settings('options.primary_providers');

/**
 * Duration of the free preview.
 *
 * @type {number} Minutes in milliseconds.
 */
const PREVIEW_DURATION = settings('options.preview_duration') * MINUTE_IN_MILLISECONDS; // eslint-disable-line max-len

/**
 * Interval at which the preview period will reset.
 *
 * @type {number} Days in milliseconds.
 */
const PREVIEW_RESET = settings('options.preview_reset')
  * unitToMilliseconds(settings('options.preview_reset_unit'));

/**
 * Adobe Primetime component.
 *
 * @class AdobePrimetime
 * @extends {Component}
 */
class AdobePrimetime extends Component {
  /**
   * Start the component.
   *
   * @param props object Component configuration..
   */
  constructor(props) {
    super(props);

    this.state = {
      authenticated: null, // Whether the user is authenticated to a provider.
      authorized: null, // Whether the user is authenticated to the resource.
      isAuthCompleted: false, // Whether the authentication and authorization verification process has been completed.
      previewPeriodExpires: null, // Time at which the free preview will expire.
      previewPeriodResets: null, // Time at which the free preview period will reset.
      previewRemaining: null, // Time remaining in the free preview period.
      mvpdList: [], // List of providers supported by Adobe Primetime.
      providersIsOpen: false, // Whether to show the providers login modal.
      playerInstance: null, // Player instance ID
      errorMessage: '', // The message is passed by Adobe Primetime authentication through this callback to the Programmer's site.
      hbaStatus: '',
      upstreamUserId: '',
      contentProvider: '',
    };

    // Bind callback functions to class.
    this.displayProviderDialog = this.displayProviderDialog.bind(this);
    this.entitlementLoaded = this.entitlementLoaded.bind(this);
    this.setAuthenticationStatus = this.setAuthenticationStatus.bind(this);
    this.setToken = this.setToken.bind(this);
    this.setProvidersIsOpen = this.setProvidersIsOpen.bind(this);
    this.tokenRequestFailed = this.tokenRequestFailed.bind(this);
    this.updatePreviewTime = this.updatePreviewTime.bind(this);
    this.setPlayerInstance = this.setPlayerInstance.bind(this);
    this.maybePauseVideo = this.maybePauseVideo.bind(this);
    this.conflictHandler = this.conflictHandler.bind(this);
    this.fetchMetadata = this.fetchMetadata.bind(this);
    this.setMetadataStatus = this.setMetadataStatus.bind(this);
    this.selectedProvider = this.selectedProvider.bind(this);
    this.setLocalSelectedProvider = this.setLocalSelectedProvider.bind(this);
  }

  /**
   * Called as part of React component lifecycle, trigger Access Enabler init.
   *
   * Theoretically this could get called more than once per page but this does
   * not appear to be an issue in practice.
   */
  componentDidMount() {
    /*
     * If the Adobe Primetime Access Enabler script hasn't been loaded, we
     * should bail here, as we won't be able to tell anything about auth status.
     */
    if (window.AccessEnabler) {
      this.setAdobeCallbacks();
      this.setPlayerInstance();
      // Enable the preview if settings allow. If a client doesn't support
      // storage (i.e. ancient browsers), the preview will never end, so don't
      // allow a preview at all.
      if (settings('options.allow_free_preview') && clientSupportsStorage()) {
        this.previewInterval = setInterval(this.updatePreviewTime, 1000);
      } else {
        this.setState({
          previewRemaining: 0,
        });
      }

      // Sets an interval to check if user is authorized to access to the livestream
      // and if not programatically pauses the video as an extra protection layer
      // Also helps the video to be paused as preview ends
      this.playerInterval = setInterval(this.maybePauseVideo, 500);
    }
  }

  /**
   * Called as part of React component lifecycle.
   */
  componentWillUnmount() {
    if (settings('options.allow_free_preview') && clientSupportsStorage()) {
      clearInterval(this.previewInterval);
    }
  }

  /**
   * Sets up and initializes Access Enabler for the page.
   */
  setAdobeCallbacks() {
    window.displayProviderDialog = this.displayProviderDialog;
    window.entitlementLoaded = this.entitlementLoaded;
    window.setAuthenticationStatus = this.setAuthenticationStatus;
    window.setToken = this.setToken;
    window.tokenRequestFailed = this.tokenRequestFailed;
    window.setMetadataStatus = this.setMetadataStatus;
    window.selectedProvider = this.selectedProvider;

    // Extract the software statement from props and init AE.
    const { swstatement } = this.props;
    window.AE = new window.AccessEnabler.AccessEnabler(swstatement);
  }

  /**
   * Callback used by Access Enabler to set authentication status.
   *
   * @param {Number} authenticationStatus Authentication status.
   * @param {String} errorCode Any error that occurred when determining authentication status. An empty string if none..
   */
  setAuthenticationStatus(authenticationStatus) {
    // 1 = authenticated, 0 = unauthenticated.
    if (0 < authenticationStatus) {
      // User is authenticated.
      this.setState({
        authenticated: true,
        errorMessage: '',
      });

      // Attempt authorization against resource ID.
      const { resourceid } = this.props;
      window.AE.getAuthorization(resourceid);
      window.AE.getSelectedProvider();
    } else {
      // User is not authenticated.
      this.setState({
        authenticated: false,
        isAuthCompleted: true,
        contentProvider: '',
      });

      // Start authentication workflow to trigger `displayProviderDialog`.
      window.AE.getAuthentication();
    }
  }

  /**
   * Called as part of Adobe Primetime lifecycle when a token request succeeds.
  */
  setToken() {
    this.setState({
      authorized: true,
      previewRemaining: false,
      isAuthCompleted: true,
    });
  }

  setProvidersIsOpen(providersIsOpen) {
    this.setState({ providersIsOpen });
  }

  /**
   * Sets Anvato player instance to state
   */
  setPlayerInstance() {
    if (settings('use_ovp')) {
      Array.from(document.querySelectorAll('.nxs-player-wrapper')).some(
        (el) => {
          const playerId = el.dataset?.player_id ?? null; // eslint-disable-line camelcase

          if (! playerId) {
            return false;
          }

          const playerInstance = window?.ovpPlayers?.[playerId] ?? null;

          if (! playerInstance) {
            return false;
          }

          this.setState({
            playerInstance,
          });

          return true;
        },
      );

      return;
    }

    Array.from(document.querySelectorAll('.ns-block-embed-anvato')).some(
      (el) => {
        const playerId = el.dataset?.anvatoPlayerId ?? null;
        if (! playerId) {
          return false;
        }

        const playerInstance = window.anvp?.[playerId] ?? null;
        if (! playerInstance) {
          return false;
        }

        this.setState({
          playerInstance,
        });

        return true;
      },
    );
  }

  /**
   * Callback used by Access Enabler to set MetaData Attributes.
   *
   * @param {String} key The key of the metadata for which the request was made.
   * @param {Bool} encrypted A flag signifying whether the "value" is encrypted or not.
   * @param {Object} data
   */
  setMetadataStatus(key, encrypted, data) {
    if (encrypted) {
      console.debug('Metadata is encrypted');
    }
    let metaDataState = {};
    switch (key) {
      case 'hba_status':
        metaDataState = { hbaStatus: data };
        break;
      case 'upstreamUserID':
      case 'userID':
        if (data) {
          metaDataState = { upstreamUserId: data };
        }
        break;
      default:
        break;
    }

    this.setState(metaDataState);
  }

  /**
   * Callback used by Access Enabler to set selected providers.
   *
   * @param {Object} provider
  */
  setLocalSelectedProvider(providerID) {
    if (providerID) {
      this.setState({ contentProvider: providerID });
    }
  }

  /**
   * Handles TVE Concurrency Error message.
  */
  conflictHandler() {
    const { contentProvider } = this.state;
    if ('Spectrum' === contentProvider) {
      // retrieved an user friendly error message
      /* eslint-disable max-len*/
      const errorMessage = `Too many streams. <br>
       You've reached the maximum number of video streams for your account.
       To view this program, close one of the streams on any other device and try again. <br>`;

      this.setState({
        authorized: false,
        isAuthCompleted: true,
        errorMessage,
      });
    }
  }

  /**
   * Callback used by Access Enabler to set MetaData Attributes.
  */
  fetchMetadata() {
    const { authenticated, authorized } = this.state;
    console.debug(`fetchMetadata executed with authenticated = ${authenticated} and authorized = ${authorized}`);
    window.AE.getMetadata('hba_status');
    window.AE.getMetadata('upstreamUserID');
  }

  /**
   * Callback used by Access Enabler to set selected providers.
   *
   * @param {Object} result
  */
  selectedProvider(result = {}) {
    if (result?.MVPD) {
      this.setState({ contentProvider: result?.MVPD });
    }
  }

  /**
   * Checks if user is authorized to access to the livestream
   * and if not programatically pauses the video as an extra protection layer
   * Also helps the video to be paused as preview ends
  */
  maybePauseVideo() {
    const {
      authenticated,
      authorized,
      playerInstance,
      previewRemaining,
    } = this.state;

    // If user is authenticated and authorized, then we dont need to check
    // whether to pause the video or not
    if (authenticated && authorized) {
      return;
    }

    // If preview ended, pause the video
    if (0 === previewRemaining) {
      playerInstance.pause();
    }
  }

  /**
   * Callback for the preview time counter.
   */
  updatePreviewTime() {
    const { previewPeriodExpires, previewPeriodResets } = this.state;
    const storage = isStorageAvailable('localStorage')
      ? 'localStorage'
      : 'sessionStorage';
    const timeNow = new Date().getTime();
    const resetStorageKey = 'adobePrimetimePreviewResets';
    const expiryStorageKey = 'adobePrimetimePreviewExpires';

    let reset = previewPeriodResets
      ?? retrieve(resetStorageKey, storage)
      ?? 0;

    let expiry = previewPeriodExpires
      ?? retrieve(expiryStorageKey, storage)
      ?? 0;

    if (
      // One of the required preview timestamps is missing.
      ! expiry || ! reset
      // The reset threshold time has passed.
      || timeNow > reset
    ) {
      expiry = timeNow + PREVIEW_DURATION;
      store(expiryStorageKey, expiry, storage);

      reset = expiry + PREVIEW_RESET;
      store(resetStorageKey, reset, storage);
    }

    this.setState({
      previewPeriodExpires: expiry,
      previewPeriodResets: reset,
    });

    // If these values aren't available by this point, something went wrong.
    if (! expiry || ! reset) {
      // eslint-disable-next-line no-console
      console.error('[Primetime] Could not initialize preview!');
      clearInterval(this.previewInterval);
      return;
    }

    const remaining = expiry > timeNow ? expiry - timeNow : 0;
    this.setState({
      previewRemaining: remaining,
    });

    // If the preview period has expired, deny access and stop tracking the
    // preview's remaining time.
    if (! remaining) {
      clearInterval(this.previewInterval);
    }

    // For analytics, we want to only send one hit every 15 seconds.
    if (0 === remaining % 15) {
      sendEvent({
        eventType: 'preview',
        previewRemaining: remaining,
      });
    }
  }

  /**
   * Called as part of Adobe Primetime lifecycle when a token request fails.
   */
  tokenRequestFailed(_resourceID, errorCode, detailedErrorMessage) {
    // retrieved an user friendly error message
    const errorMessage = getErrorMessage(errorCode, detailedErrorMessage);

    this.setState({
      authorized: false,
      isAuthCompleted: true,
      errorMessage,
    });
  }

  /**
   * Callback invoked by Access Enabler to handle provider selection.
   *
   * @param {Array} mvpdList Array of MVPD providers from Adobe Primetime.
   */
  displayProviderDialog(mvpdList) {
    if (Array.isArray(mvpdList)) {
      // Sort mvpdList by provider name in alphabetical order.
      mvpdList.sort((provider1, provider2) => {
        const provider1Name = provider1?.displayName?.toLowerCase() || '';
        const provider2Name = provider2?.displayName?.toLowerCase() || '';

        return provider1Name?.localeCompare(provider2Name);
      });
    }

    this.setState({
      mvpdList,
    });
  }

  /**
   * Callback used to set requester ID after Access Enabler is loaded.
   */
  entitlementLoaded() {
    const { requestorid } = this.props;
    window.AE.setRequestor(requestorid);
  }

  /**
   * Render the components used for Adobe Primetime integration.
   */
  render() {
    const {
      authenticated,
      authorized,
      isAuthCompleted,
      previewRemaining,
      mvpdList,
      providersIsOpen,
      errorMessage,
      hbaStatus,
      upstreamUserId,
      contentProvider,
    } = this.state;

    const showProviders = () => this.setProvidersIsOpen(true);

    return (
      <>
        <Providers
          all={mvpdList}
          isOpen={providersIsOpen}
          primary={PRIMARY_PROVIDERS}
          setIsOpen={(value) => this.setProvidersIsOpen(value)}
          setLocalSelectedProvider={(value) => this.setLocalSelectedProvider(value)}
        />

        <FooterLinkPortal>
          <>
            {authenticated ? (
              <button onClick={logout} type="button">
                {settings('strings.account.logout')}
              </button>
            ) : (
              <button onClick={showProviders} type="button">
                {settings('strings.account.login')}
              </button>
            )}
          </>
        </FooterLinkPortal>

        <PlayerOverlay
          authenticated={authenticated}
          authorized={authorized}
          isAuthCompleted={isAuthCompleted}
          previewRemaining={previewRemaining}
          onConnectClick={showProviders}
          errorMessage={errorMessage}
        />

        <ConcurrencyHandler
          authenticated={authenticated}
          authorized={authorized}
          hbaStatus={hbaStatus}
          upstreamUserId={upstreamUserId}
          selectedProviderId={contentProvider}
          fetchMetadata={() => this.fetchMetadata()}
          conflictHandler={() => this.conflictHandler()}
        />
      </>
    );
  }
}

AdobePrimetime.propTypes = {
  requestorid: PropTypes.string.isRequired,
  resourceid: PropTypes.string.isRequired,
  swstatement: PropTypes.string.isRequired,
};

export default AdobePrimetime;
